import React, {useCallback, useMemo, useState} from 'react';

import localStorage from 'LocalStorage';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal, {ModalProps} from 'ui-library/modals/Modal';
import uniqueId from 'util/uniqueId';

const MAXIMIZED = 'max';

interface ExplicitProps {
  startMaximized?: boolean;
  rememberExpandedSetting?: never;
}

interface PropsWithUserSetting {
  // User setting for this modal can be saved to local storage
  modalKey: string;
  rememberExpandedSetting: boolean;
  startMaximized?: never;
}

export function ExpandableModal(props: ModalProps & (ExplicitProps | PropsWithUserSetting)): JSX.Element {
  const storageKey = props.rememberExpandedSetting ? `expandable_${props.modalKey}` : null;
  const startMaximized = storageKey
    ? localStorage.getItem(storageKey) === MAXIMIZED
    : Boolean(props.startMaximized);
  const [maximized, _setMaximized] = useState(startMaximized);
  const cid = useMemo(() => uniqueId('modal'), []);

  const setMaximized = useCallback(
    (newMaximizedSetting: boolean) => {
      _setMaximized(newMaximizedSetting);
      if (storageKey) {
        if (newMaximizedSetting) {
          localStorage.setItem(storageKey, MAXIMIZED);
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.rememberExpandedSetting, props.modalKey]
  );

  return (
    <Modal
      additionalHeaderIcons={[
        <BenchlingIcon
          altText={maximized ? 'Collapse' : 'Expand'}
          aria-controls={cid}
          aria-expanded={maximized}
          icon={maximized ? 'collapse-right-full' : 'expand-right-full'}
          onClick={() => {
            setMaximized(!maximized);
          }}
          role="button"
          className="modal-header-icon"
          key="toggle-size"
        />,
      ]}
      cidOverride={cid}
      fullHeight={maximized}
      fullWidth={maximized}
      flexBody={true}
      flexContent={true}
      {...props}
    />
  );
}
